import { CaretRightOutlined } from "@ant-design/icons";
import { Button, Tooltip, message } from "antd";
import React from "react";
import { NoteType } from "../../domain/notes.domain";
import { PRACTICE_AREA } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import { getNoteAudioPlaybackURL } from "../../service/notes.service";
import { ModalityMode, type TNotes } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";

// Only these modalities produce a recording; write-ups (and edit-regenerated
// notes, which are stored as write) have no audio of their own.
const AUDIO_MODALITIES: number[] = [ModalityMode.audio, ModalityMode.dictate, ModalityMode.upload];

// The recordings bucket deletes objects at age 7 days (lifecycle rule), so
// past that the file is gone. Inclusive: a note exactly 7 days old still
// plays; older ones get a greyed-out control that explains the policy on
// hover. The backend serves whatever still exists, so this only gates the UI.
const AUDIO_RETENTION_DAYS = 7;
const AUDIO_RETENTION_MS = AUDIO_RETENTION_DAYS * 24 * 60 * 60 * 1000;
const RETENTION_EXPIRED_MESSAGE = `Recordings are kept for ${AUDIO_RETENTION_DAYS} days, so this encounter's audio is no longer available.`;

type TNoteAudioPlayerProps = {
  note: Pick<TNotes, "id" | "status" | "error_status" | "modality_type" | "note_type" | "uploaded_at" | "is_incapture">;
  style?: React.CSSProperties;
};

/**
 * Playback of an encounter's audio recording, available to Medicine accounts —
 * behavioral-health sessions never show it, matching the server-side check on
 * the presigned-URL endpoint. Renders nothing for accounts without the feature
 * or notes without audio; notes past the 7-day
 * retention window show a disabled control with the policy on hover. The
 * presigned GCS URL is fetched lazily on the first Play click so listings
 * don't hit the endpoint on render.
 */
const NoteAudioPlayer = ({ note, style }: TNoteAudioPlayerProps) => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const { isDemoPath } = useAppSelector((state) => state.appState);
  const [loading, setLoading] = React.useState(false);
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const withinRetention =
    Boolean(note.uploaded_at) && Date.now() - new Date(note.uploaded_at).getTime() <= AUDIO_RETENTION_MS;

  // A recorder-first capture is already in storage once Stop finishes, so it plays before details are added.
  const isStoppedCaptureAwaitingDetails = note.status === 2 && note.note_type == null && note.is_incapture === false;

  const eligible =
    userInfo?.practice_area === PRACTICE_AREA.MEDICINE &&
    userInfo?.can_view_transcript &&
    !isDemoPath &&
    (note.status === 1 || isStoppedCaptureAwaitingDetails) &&
    note.error_status === 0 &&
    note.note_type !== NoteType.EHR_PRIOR_NOTE &&
    AUDIO_MODALITIES.includes(note.modality_type);

  if (!eligible) return null;

  if (!withinRetention) {
    return (
      <div style={style} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={RETENTION_EXPIRED_MESSAGE}>
          <Button size="small" icon={<CaretRightOutlined />} disabled>
            Play audio
          </Button>
        </Tooltip>
      </div>
    );
  }

  const handlePlayClick = async () => {
    setLoading(true);
    trackEvent(EventType.PLAY_NOTE_AUDIO, { recordingId: note.id });
    try {
      const response = await getNoteAudioPlaybackURL(note.id);
      setAudioUrl(response.data.url);
    } catch (error) {
      const responseStatus = (error as { response?: { status?: number } })?.response?.status;
      message.error(
        responseStatus === 404
          ? "The audio recording for this encounter is no longer available. Recordings are kept for 7 days."
          : "Could not load the audio recording. Please try again.",
      );
      console.error("Error fetching note audio URL:", error);
    } finally {
      setLoading(false);
    }
  };

  // MediaRecorder-produced webm files carry no duration header, so the element
  // reports Infinity; seeking to the end once forces the browser to compute it.
  const handleLoadedMetadata = () => {
    const el = audioRef.current;
    if (!el || el.duration !== Number.POSITIVE_INFINITY) return;
    const resetToStart = () => {
      el.removeEventListener("timeupdate", resetToStart);
      el.currentTime = 0;
      el.play().catch(() => {});
    };
    el.addEventListener("timeupdate", resetToStart);
    el.currentTime = Number.MAX_SAFE_INTEGER;
  };

  // Presigned URLs expire after an hour; dropping back to the Play button lets
  // the user mint a fresh one instead of staring at a dead scrubber.
  const handleAudioError = () => {
    setAudioUrl(null);
    message.error("Audio playback stopped. Click Play audio to reload.");
  };

  return (
    // stopPropagation: in listings the whole row navigates on click, and the
    // native audio controls must not open the note.
    <div style={style} onClick={(e) => e.stopPropagation()}>
      {audioUrl ? (
        <audio
          ref={audioRef}
          controls
          autoPlay
          src={audioUrl}
          style={{ width: "100%", height: 32, display: "block" }}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleAudioError}
        />
      ) : (
        <Button size="small" icon={<CaretRightOutlined />} loading={loading} onClick={handlePlayClick}>
          Play audio
        </Button>
      )}
    </div>
  );
};

export default NoteAudioPlayer;
